package com.kanati.admin.features.reports

import androidx.compose.runtime.*
import com.kanati.admin.api.AdminApiClient
import com.kanati.admin.components.*
import com.kanati.admin.state.*
import com.kanati.shared.commerce.*
import org.jetbrains.compose.web.attributes.selected
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

@Composable
fun ConcessionOrdersPanel(apiClient: AdminApiClient, district: SelectedDistrictContext, school: SelectedSchoolContext, theme: MaterialThemePalette) {
    key(apiClient, district.id, school.id) {
        ScopedConcessionOrdersPanel(apiClient, district, school, theme)
    }
}

@Composable
private fun ScopedConcessionOrdersPanel(apiClient: AdminApiClient, district: SelectedDistrictContext, school: SelectedSchoolContext, theme: MaterialThemePalette) {
    val orders = remember { ReadState<List<ConcessionOrderDto>>() }
    val detail = remember { ReadState<ConcessionOrderDto?>() }
    var status by remember { mutableStateOf("") }
    var source by remember { mutableStateOf("") }
    var appliedFilters by remember { mutableStateOf("" to "") }
    var revision by remember { mutableStateOf(0) }
    var lookupKind by remember { mutableStateOf("order") }
    var lookupId by remember { mutableStateOf("") }
    var lookup by remember { mutableStateOf<Pair<String, String>?>(null) }
    var lookupRevision by remember { mutableStateOf(0) }

    LaunchedEffect(appliedFilters, revision) {
        orders.refresh("Concession orders could not be loaded. Check school access and sign-in, then refresh. Order visibility is unknown.") {
            apiClient.getActiveConcessionOrders(
                district.id, school.id,
                appliedFilters.first.takeIf(String::isNotEmpty)?.let(ConcessionOrderStatus::valueOf),
                appliedFilters.second.takeIf(String::isNotEmpty)?.let(ConcessionOrderSource::valueOf),
            ).onEach { it.requireReviewScope(district.id, school.id) }
        }
    }
    LaunchedEffect(lookup, lookupRevision) {
        val request = lookup ?: return@LaunchedEffect
        detail.refresh("Order lookup failed. Check school access and sign-in, then retry. This does not confirm that an order is absent.") {
            findConcessionOrder {
                val order = if (request.first == "sale") apiClient.getConcessionOrderBySale(district.id, school.id, request.second)
                else apiClient.getConcessionOrder(district.id, school.id, request.second)
                order.requireReviewScope(district.id, school.id)
            }
        }
    }

    SectionCard("Concession orders", theme, description = "Manager review for ${school.name}.", headerActions = {
        ActionButton(if (orders.loading) "Loading..." else "Apply / refresh orders", {
            appliedFilters = status to source
            revision++
        }, theme, isDisabled = orders.loading, isPrimary = false)
    }) {
        P { Text(concessionReviewNotice) }
        P { Text(concessionFinancialNotice) }
        P { Text(concessionMobileNotice) }
        H3 { Text("Active order snapshot") }
        P { Text(concessionListLimit) }
        FieldRow {
            OrderSelect("Concession status", status, listOf("" to "All active statuses") + activeConcessionStatuses.map { it.name to concessionStatusLabel(it) }, theme) { status = it }
            OrderSelect("Concession source", source, listOf("" to "All sources") + ConcessionOrderSource.entries.map { it.name to concessionSourceLabel(it) }, theme) { source = it }
        }
        P { Text("Applied: ${appliedFilters.first.takeIf(String::isNotEmpty)?.let { concessionStatusLabel(ConcessionOrderStatus.valueOf(it)) } ?: "All active statuses"} · ${appliedFilters.second.takeIf(String::isNotEmpty)?.let { concessionSourceLabel(ConcessionOrderSource.valueOf(it)) } ?: "All sources"}.") }
        when {
            orders.loading -> StatusBanner("Loading concession orders...", theme)
            orders.error != null -> StatusBanner(orders.error!!, theme, BannerTone.Error)
            orders.value?.isEmpty() == true -> StatusBanner("No active concession orders match these filters. This does not establish school activation or absence of past orders.", theme)
            else -> {
                P { Text("${orders.value.orEmpty().size} active orders in this snapshot. Select Review for current details.") }
                OrderTable(listOf("Pickup", "Source", "Status", "Pickup name", "Items", "Created (UTC)", "Details"), theme) {
                    orders.value.orEmpty().forEach { order ->
                        Tr {
                            listOf(order.displayNumber, concessionSourceLabel(order.source), concessionStatusLabel(order.status), order.pickupName ?: "Not supplied", concessionLineSummary(order), order.createdAt).forEach { value ->
                                OrderCell(theme) { Text(value) }
                            }
                            OrderCell(theme) {
                                ActionButton("Review ${order.displayNumber}", {
                                    lookupKind = "order"
                                    lookupId = order.id
                                    lookup = "order" to order.id
                                    lookupRevision++
                                }, theme, isPrimary = false)
                            }
                        }
                    }
                }
            }
        }
        H3 { Text("Order lookup · including completed and canceled") }
        P { Text("Use an order ID or sale ID from a recorded transaction. Pickup display numbers are not lookup IDs. Lookup is independent of the active filters above.") }
        FieldRow {
            OrderSelect("Lookup by", lookupKind, listOf("order" to "Order ID", "sale" to "Sale ID"), theme) { lookupKind = it }
            TextField(theme, if (lookupKind == "sale") "Sale ID" else "Order ID", lookupId, onValueChange = { lookupId = it })
            ActionButton("Look up / refresh order", { lookup = lookupKind to lookupId.trim(); lookupRevision++ }, theme, isDisabled = lookupId.isBlank(), isPrimary = false)
        }
        lookup?.let { P { Text("Requested ${if (it.first == "sale") "sale" else "order"} ID: ${it.second}") } }
        when {
            lookup == null -> P { Text("Choose Review above or enter an ID to load an order.") }
            detail.loading -> StatusBanner("Loading order details...", theme)
            detail.error != null -> StatusBanner(detail.error!!, theme, BannerTone.Error)
            detail.value == null -> StatusBanner("No concession order was found for this ID in the selected school. This is not evidence of an unpaid sale; historical sales are not backfilled into fulfillment.", theme)
            else -> ConcessionOrderDetails(detail.value!!, theme)
        }
    }
}

@Composable
private fun ConcessionOrderDetails(order: ConcessionOrderDto, theme: MaterialThemePalette) {
    H3 { Text("Order ${order.displayNumber}") }
    P { Text("Backend snapshot · timestamps are UTC. Payment reference is not supplied; use the sale reference for reconciliation.") }
    if (order.status == ConcessionOrderStatus.CANCELED) StatusBanner("Canceled fulfillment · no refund or stock restoration. Reason: ${order.cancellationReason ?: "Not supplied"}", theme)
    OrderTable(listOf("Order detail", "Value"), theme) {
        concessionOrderFacts(order).forEach { (label, value) ->
            Tr {
                Th({ attr("scope", "row"); style { padding(8.px) } }) { Text(label) }
                OrderCell(theme) { Text(value) }
            }
        }
    }
    P { Text("Sale line snapshots. Line subtotals exclude sale-level tax and discounts; they are not the final paid total.") }
    OrderTable(listOf("Item", "Product ID", "Quantity", "Unit price", "Line subtotal", "Taxable"), theme) {
        order.lineItems.forEach { line ->
            Tr {
                listOf(line.productNameSnapshot, line.productId, line.quantity.toString(), formatMoney(line.unitPriceAmount.toLong()), formatMoney(line.lineSubtotalAmount.toLong()), if (line.taxable) "Yes" else "No").forEach { value ->
                    OrderCell(theme) { Text(value) }
                }
            }
        }
    }
}

@Composable
private fun OrderSelect(label: String, value: String, options: List<Pair<String, String>>, theme: MaterialThemePalette, onChange: (String) -> Unit) {
    Label {
        Text("$label ")
        Select({
            attr("aria-label", label)
            onChange { onChange(it.value.orEmpty()) }
            style { padding(12.px); borderRadius(14.px); property("max-width", "100%"); property("border", "1px solid ${theme.outline}"); property("background", theme.surface); property("color", theme.onSurface) }
        }) {
            options.forEach { (id, title) -> Option(id, { if (value == id) selected() }) { Text(title) } }
        }
    }
}

@Composable
private fun OrderTable(headers: List<String>, theme: MaterialThemePalette, content: @Composable () -> Unit) {
    Div({ style { property("overflow-x", "auto") } }) {
        Table({ style { property("width", "100%"); property("border-collapse", "collapse"); property("text-align", "left") } }) {
            Thead { Tr { headers.forEach { Th({ attr("scope", "col"); style { padding(8.px); property("border-bottom", "1px solid ${theme.outlineVariant}") } }) { Text(it) } } } }
            Tbody { content() }
        }
    }
}

@Composable
private fun OrderCell(theme: MaterialThemePalette, content: @Composable () -> Unit) {
    Td({ style { padding(8.px); property("vertical-align", "top"); property("border-top", "1px solid ${theme.outlineVariant}"); property("overflow-wrap", "anywhere") } }) { content() }
}
